import { useEffect, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/contexts/AuthContext";
import { DEV_BYPASS_AUTH } from "@/lib/auth-config";
import { NanaeLogoSymbol, NanaeWordmark } from "@/components/qv3/NanaeLogo";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  if (DEV_BYPASS_AUTH) return <>{children}</>;
  return <Guard>{children}</Guard>;
}

function Guard({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/login" });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Vérification de votre identité"
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "#000000",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 24,
          zIndex: 30,
        }}
      >
        <div
          style={{
            animation: "nanaeAuthPulse 1.4s ease-in-out infinite",
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <NanaeLogoSymbol size="lg" />
          <NanaeWordmark size="lg" as="h1" />
        </div>
        <p
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: "italic",
            fontSize: 16,
            color: "#FF1744",
            letterSpacing: "0.5px",
            margin: 0,
          }}
        >
          Le Conseil vérifie votre identité…
        </p>
        <style>{`
          @keyframes nanaeAuthPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }
        `}</style>
      </div>
    );
  }

  if (!user) return null;
  return <>{children}</>;
}
